import type { App } from 'vue';
import { registerLicense } from '@syncfusion/ej2-base';
import {
  SpreadsheetPlugin,
  SheetsPlugin,
  SheetPlugin,
  RangesPlugin,
  RangePlugin,
  ColumnsPlugin,
  ColumnPlugin,
  RowsPlugin,
  RowPlugin,
  CellsPlugin,
  CellPlugin
} from '@syncfusion/ej2-vue-spreadsheet';

const SYNCFUSION_LICENSE_KEY =
  'Ngo9BigBOggjHTQxAR8/V1JFaF5cXGRCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdmWH9fdXVVRmNZUENwVkZWYEg=';

const GLOBAL_STATE_KEY = '__adam_syncfusion_state__';

type SyncfusionState = {
  licenseRegistered: boolean;
  pluginsRegistered: boolean;
};

const resolveState = (): SyncfusionState => {
  const globalObject = globalThis as typeof globalThis & Record<string, SyncfusionState | undefined>;

  if (!globalObject[GLOBAL_STATE_KEY]) {
    globalObject[GLOBAL_STATE_KEY] = {
      licenseRegistered: false,
      pluginsRegistered: false
    };
  }

  return globalObject[GLOBAL_STATE_KEY]!;
};

function ensureLicenseRegistration(): void {
  const state = resolveState();

  if (state.licenseRegistered) {
    return;
  }

  registerLicense(SYNCFUSION_LICENSE_KEY);
  state.licenseRegistered = true;
}

export function setupSyncfusion(app: App): void {
  ensureLicenseRegistration();

  const state = resolveState();

  if (state.pluginsRegistered) {
    return;
  }

  // Helper to safely register plugins by checking if components are already registered
  const safeUsePlugin = (plugin: any, componentNames: string[]) => {
    // Check if any of the components from this plugin are already registered
    const alreadyRegistered = componentNames.some(name => {
      return app.component(name) !== undefined;
    });

    if (!alreadyRegistered) {
      app.use(plugin);
    }
  };

  // Register main spreadsheet plugin
  safeUsePlugin(SpreadsheetPlugin, ['ejs-spreadsheet']);

  // Register all directive plugins needed for child components
  safeUsePlugin(SheetsPlugin, ['e-sheets']);
  safeUsePlugin(SheetPlugin, ['e-sheet']);
  safeUsePlugin(RangesPlugin, ['e-ranges']);
  safeUsePlugin(RangePlugin, ['e-range']);
  safeUsePlugin(ColumnsPlugin, ['e-columns']);
  safeUsePlugin(ColumnPlugin, ['e-column']);
  safeUsePlugin(RowsPlugin, ['e-rows']);
  safeUsePlugin(RowPlugin, ['e-row']);
  safeUsePlugin(CellsPlugin, ['e-cells']);
  safeUsePlugin(CellPlugin, ['e-cell']);

  state.pluginsRegistered = true;
}
